import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  CheckCircle,
  XCircle,
  Package,
  User,
  Calendar,
  DollarSign,
  FileText,
  Image as ImageIcon,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { ProductApprovalAPI } from '../../lib/api';
import { ProductApprovalDetails, ProductApprovalRequest } from '../../lib/types';
import { toast } from '../ui/use-toast';
import { formatCurrency, formatDate } from '../../lib/utils';

interface ProductApprovalDetailProps {
  productId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onApprovalAction: () => void;
}

const ProductApprovalDetail: React.FC<ProductApprovalDetailProps> = ({
  productId,
  isOpen,
  onClose,
  onApprovalAction,
}) => {
  const [productDetails, setProductDetails] = useState<ProductApprovalDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [approvalFormVisible, setApprovalFormVisible] = useState(false);
  const [rejectionFormVisible, setRejectionFormVisible] = useState(false);

  // Form states
  const [basePrice, setBasePrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [taxRate, setTaxRate] = useState('18');
  const [discount, setDiscount] = useState('0');
  const [rejectionReason, setRejectionReason] = useState('');

  // Original supplier values for comparison
  const [originalValues, setOriginalValues] = useState({
    suggestedPrice: 0,
    gstRate: 0,
    calculatedBasePrice: 0
  });

  useEffect(() => {
    if (productId && isOpen) {
      fetchProductDetails();
    } else {
      setProductDetails(null);
      resetForms();
    }
  }, [productId, isOpen]);

  const fetchProductDetails = async () => {
    if (!productId) return;

    setLoading(true);
    try {
      const response = await ProductApprovalAPI.getProductApprovalDetails(productId);
      setProductDetails(response.data);

      // Pre-fill pricing based on supplier's data
      const suggestedPrice = response.data.product.suggestedPrice;
      const gstRate = response.data.product.gstRate || 0;

      // Store original values for comparison
      const calculatedBasePrice = gstRate > 0
        ? Math.round((suggestedPrice / (1 + gstRate / 100)) * 100) / 100
        : suggestedPrice;

      setOriginalValues({
        suggestedPrice,
        gstRate,
        calculatedBasePrice
      });

      // If GST rate exists, assume supplier price is inclusive of tax
      if (gstRate > 0) {
        // Calculate base price (excluding tax) from inclusive price
        setBasePrice(calculatedBasePrice.toString()); // Round to 2 decimal places
        setSellingPrice(suggestedPrice.toString()); // Selling price remains the supplier's suggested price
        setTaxRate(gstRate.toString()); // Use supplier's GST rate
      } else {
        // If no GST rate, treat suggested price as base price
        setBasePrice(suggestedPrice.toString());
        setSellingPrice(suggestedPrice.toString());
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch product details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate selling price dynamically when base price or tax rate changes
  const calculateSellingPrice = (basePriceValue: string, taxRateValue: string) => {
    const base = parseFloat(basePriceValue || '0');
    const tax = parseFloat(taxRateValue || '0');
    if (base > 0) {
      const calculatedSellingPrice = base + (base * tax / 100);
      return (Math.round(calculatedSellingPrice * 100) / 100).toString();
    }
    return basePriceValue;
  };

  // Handle base price change
  const handleBasePriceChange = (value: string) => {
    setBasePrice(value);
    const newSellingPrice = calculateSellingPrice(value, taxRate);
    setSellingPrice(newSellingPrice);
  };

  // Handle tax rate change
  const handleTaxRateChange = (value: string) => {
    setTaxRate(value);
    const newSellingPrice = calculateSellingPrice(basePrice, value);
    setSellingPrice(newSellingPrice);
  };

  // Check for changes from original supplier values
  const getChanges = () => {
    const changes = [];

    if (Math.abs(parseFloat(basePrice || '0') - originalValues.calculatedBasePrice) > 0.01) {
      changes.push(`Base Price: ₹${originalValues.calculatedBasePrice} → ₹${parseFloat(basePrice || '0').toFixed(2)}`);
    }

    if (Math.abs(parseFloat(sellingPrice || '0') - originalValues.suggestedPrice) > 0.01) {
      changes.push(`Selling Price: ₹${originalValues.suggestedPrice} → ₹${parseFloat(sellingPrice || '0').toFixed(2)}`);
    }

    if (Math.abs(parseFloat(taxRate || '0') - originalValues.gstRate) > 0.01) {
      changes.push(`Tax Rate: ${originalValues.gstRate}% → ${parseFloat(taxRate || '0')}%`);
    }

    if (parseFloat(discount || '0') > 0) {
      changes.push(`Discount: 0% → ${parseFloat(discount || '0')}%`);
    }

    return changes;
  };

  const resetForms = () => {
    setBasePrice('');
    setSellingPrice('');
    setTaxRate('18');
    setDiscount('0');
    setRejectionReason('');
    setApprovalFormVisible(false);
    setRejectionFormVisible(false);
    setOriginalValues({
      suggestedPrice: 0,
      gstRate: 0,
      calculatedBasePrice: 0
    });
  };

  const handleApprove = async () => {
    if (!productId) return;

    setSubmitting(true);
    try {
      const approvalData: ProductApprovalRequest = {
        action: 'approve',
        basePrice: parseFloat(basePrice),
        sellingPrice: parseFloat(sellingPrice),
        taxRate: parseFloat(taxRate),
        discount: parseFloat(discount)
      };

      await ProductApprovalAPI.approveOrRejectProduct(productId, approvalData);

      toast({
        title: "Success",
        description: "Product approved successfully",
        variant: "default",
      });

      setApprovalFormVisible(false);
      onApprovalAction();
    } catch (error) {
      console.error('Error approving product:', error);
      toast({
        title: "Error",
        description: "Failed to approve product",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!productId || !rejectionReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a rejection reason",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const rejectionData: ProductApprovalRequest = {
        action: 'reject',
        rejectionReason: rejectionReason.trim()
      };

      await ProductApprovalAPI.approveOrRejectProduct(productId, rejectionData);

      toast({
        title: "Success",
        description: "Product rejected successfully",
        variant: "default",
      });

      setRejectionFormVisible(false);
      onApprovalAction();
    } catch (error) {
      console.error('Error rejecting product:', error);
      toast({
        title: "Error",
        description: "Failed to reject product",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Early return only if no productId
  if (!productId) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 flex-shrink-0">
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              <Package className="h-6 w-6 text-primary" />
              Product Review
            </DialogTitle>
            <DialogDescription className="text-base">
              Review supplier product submission for approval
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-5 w-32" />
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : productDetails ? (
            <div className="overflow-y-auto flex-1 pr-2">
              {/* Action Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 bg-accent/50 rounded-lg">
                <div className="flex items-center gap-3 mb-3 sm:mb-0">
                  <Badge variant="warning" className="text-sm px-3 py-1">
                    {productDetails.product.status.charAt(0).toUpperCase() + productDetails.product.status.slice(1)}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Submitted: {formatDate(productDetails.product.createdAt)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setApprovalFormVisible(true);
                      setRejectionFormVisible(false);
                    }}
                    className="border-green-600 text-green-600 hover:bg-green-50 hover:border-green-700 hover:text-green-700"
                    disabled={productDetails.product.status !== 'pending'}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Product
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRejectionFormVisible(true);
                      setApprovalFormVisible(false);
                    }}
                    className="border-red-600 text-red-600 hover:bg-red-50 hover:border-red-700 hover:text-red-700"
                    disabled={productDetails.product.status !== 'pending'}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Product
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="details" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="images" className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Images ({productDetails.product.images.length})
                  </TabsTrigger>
                  <TabsTrigger value="specifications" className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Specifications
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Product Information */}
                    <Card className="dashboard-card">
                      <CardContent className="space-y-3 pt-6">
                        <div>
                          <span className="text-muted-foreground">Product Name:</span>
                          <div className="font-medium text-lg">{productDetails.product.name}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Description:</span>
                          <div className="font-medium">{productDetails.product.description}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Category:</span>
                          <div className="font-medium">{productDetails.product.category.name}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-muted-foreground">Unit:</span>
                            <div className="font-medium">{productDetails.product.unit}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Suggested Price:</span>
                            <div className="font-medium text-primary text-lg">
                              {formatCurrency(productDetails.product.suggestedPrice)}
                              {productDetails.product.gstRate && productDetails.product.gstRate > 0 && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (incl. {productDetails.product.gstRate}% GST)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <span className="text-muted-foreground">Min Order:</span>
                            <div className="font-medium">{productDetails.product.minOrderQuantity} {productDetails.product.unit}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Max Order:</span>
                            <div className="font-medium">{productDetails.product.maxOrderQuantity} {productDetails.product.unit}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">GST Rate:</span>
                            <div className="font-medium">{productDetails.product.gstRate || 0}%</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Supplier Information */}
                    <Card className="dashboard-card">
                      <CardContent className="space-y-3 pt-6">
                        <div>
                          <span className="text-muted-foreground">Business Name:</span>
                          <div className="font-medium">{productDetails.product.createdBySupplier.businessName}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Owner Name:</span>
                          <div className="font-medium">{productDetails.product.createdBySupplier.ownerName}</div>
                        </div>
                        {productDetails.product.createdBySupplier.contact && (
                          <div>
                            <span className="text-muted-foreground">Contact:</span>
                            <div className="font-medium">{productDetails.product.createdBySupplier.contact}</div>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">Submission Date:</span>
                          <div className="font-medium flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {formatDate(productDetails.product.createdAt)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="images" className="space-y-4">
                  <Card>
                    <CardContent className="pt-6">
                      {productDetails.product.images.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {productDetails.product.images.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image.url}
                                alt={`Product image ${index + 1}`}
                                className="w-full h-32 object-cover rounded-md border"
                              />
                              {image.isDefault && (
                                <Badge
                                  variant="success"
                                  className="absolute top-2 left-2 text-xs"
                                >
                                  Default
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No images available</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="specifications" className="space-y-4">
                  <Card>
                    <CardContent className="pt-6">
                      {productDetails.product.specifications.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {productDetails.product.specifications.map((spec, index) => (
                            <div key={index} className="border rounded-md p-3">
                              <div className="font-medium text-sm text-muted-foreground">
                                {spec.name}
                              </div>
                              <div className="font-medium">{spec.value}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No specifications available</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <Card className="border-destructive">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <p className="text-destructive font-medium">Failed to load product details</p>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Form - Separate Dialog */}
      <Dialog open={approvalFormVisible} onOpenChange={setApprovalFormVisible}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="bg-green-50 border-b border-green-200 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              Approve Product
            </DialogTitle>
            <DialogDescription className="text-green-700 mt-1">
              Set pricing details for <strong>{productDetails?.product.name}</strong>
            </DialogDescription>
            {productDetails?.product.gstRate && productDetails.product.gstRate > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Supplier's suggested price (₹{productDetails.product.suggestedPrice}) appears to be inclusive of {productDetails.product.gstRate}% GST.
                  Base price has been calculated as ₹{parseFloat(basePrice || '0').toFixed(2)} (excluding tax).
                  You can modify these values as needed.
                </p>
              </div>
            )}
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Base Price * <span className="text-xs text-gray-500">(Cost price)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={basePrice}
                  onChange={(e) => handleBasePriceChange(e.target.value)}
                  placeholder="Enter base price"
                  className="focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Selling Price <span className="text-xs text-gray-500">(Customer price)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  placeholder="Enter selling price"
                  className="focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Tax Rate (%) <span className="text-xs text-gray-500">(GST/VAT)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxRate}
                  onChange={(e) => handleTaxRateChange(e.target.value)}
                  placeholder="Enter tax rate"
                  className="focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Default Discount (%) <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="Enter discount"
                  className="focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            {/* Price Preview */}
            {basePrice && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-semibold text-green-800 mb-2">Price Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-green-700">Base Price (excl. tax):</span>
                    <span className="font-semibold ml-2">₹{parseFloat(basePrice || '0').toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-green-700">Tax Amount ({taxRate}%):</span>
                    <span className="font-semibold ml-2">₹{(parseFloat(sellingPrice || '0') - parseFloat(basePrice || '0')).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-green-700">Selling Price (incl. tax):</span>
                    <span className="font-semibold ml-2">₹{parseFloat(sellingPrice || '0').toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-green-700">Final Price (after discount):</span>
                    <span className="font-semibold ml-2 text-lg text-green-800">
                      ₹{(() => {
                        const sellingPriceInclusiveTax = parseFloat(sellingPrice || '0');
                        const disc = parseFloat(discount || '0');
                        const final = sellingPriceInclusiveTax * (1 - disc / 100);
                        return final.toLocaleString();
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Changes Warning */}
            {getChanges().length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Changes Made from Supplier's Original Values
                </h4>
                <ul className="text-xs text-yellow-700 space-y-1">
                  {getChanges().map((change, index) => (
                    <li key={index}>• {change}</li>
                  ))}
                </ul>
                <p className="text-xs text-yellow-600 mt-2 font-medium">
                  Please confirm these changes before approving the product.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 p-6 pt-0 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setApprovalFormVisible(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting || !basePrice || parseFloat(basePrice) <= 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Approve Product
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Form - Separate Dialog */}
      <Dialog open={rejectionFormVisible} onOpenChange={setRejectionFormVisible}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="bg-red-50 border-b border-red-200 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-red-800">
              <XCircle className="h-5 w-5" />
              Reject Product
            </DialogTitle>
            <DialogDescription className="text-red-700 mt-1">
              Provide feedback for <strong>{productDetails?.product.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Rejection Reason *
              </label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejection. This will help the supplier improve their product submission..."
                rows={6}
                className="focus:ring-red-500 focus:border-red-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                {rejectionReason.length}/500 characters
              </p>
            </div>

            {/* Common rejection reasons */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Quick Suggestions:
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  "Insufficient product information",
                  "Poor image quality",
                  "Unrealistic pricing",
                  "Missing specifications",
                  "Category mismatch"
                ].map((reason) => (
                  <Button
                    key={reason}
                    variant="outline"
                    size="sm"
                    onClick={() => setRejectionReason(prev => prev ? `${prev}; ${reason}` : reason)}
                    className="text-xs h-7"
                  >
                    + {reason}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 pt-0 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setRejectionFormVisible(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={submitting || !rejectionReason.trim() || rejectionReason.trim().length < 10}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject Product
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductApprovalDetail;